import React, { useState, useEffect, useCallback, useMemo } from "react";
import config from "../../config";
import "./OfflineOrderModal.css";

function OfflineOrderModal({ selectedProducts, onClose, onSuccess }) {
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingPincode, setShippingPincode] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [salesChannel, setSalesChannel] = useState("SHOP");
  const [createdByName, setCreatedByName] = useState("");
  const [orderCart, setOrderCart] = useState([]);
  const [variantSelections, setVariantSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const storedName = localStorage.getItem("adminName") || "";
    setCreatedByName(storedName);
  }, []);

  useEffect(() => {
    setVariantSelections((prevSelections) => {
      const nextSelections = { ...prevSelections };
      selectedProducts.forEach((product) => {
        product.variants?.forEach((variant) => {
          if (!nextSelections[variant.variantId]) {
            nextSelections[variant.variantId] = {
              quantity: "1",
              sellingPrice: String(variant.offerPrice ?? variant.originalPrice ?? 0)
            };
          }
        });
      });
      return nextSelections;
    });
  }, [selectedProducts]);

  const getMainImage = (product) => {
    return (
      product.images?.find((img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY")?.image_url ||
      product.images?.find((img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY")?.imageUrl ||
      product.images?.[0]?.image_url ||
      product.images?.[0]?.imageUrl ||
      "https://via.placeholder.com/200x200?text=No+Image"
    );
  };

  const normalizePhoneInput = (value) => {
    return value.replace(/\D/g, "").slice(0, 10);
  };

  const sanitizeIntegerValue = (value, maxLength = 6) => {
    return value.replace(/\D/g, "").slice(0, maxLength);
  };

  const sanitizeDecimalValue = (value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (cleaned === ".") return "";
    const [integer, ...fractionParts] = cleaned.split(".");
    return fractionParts.length > 0 ? `${integer}.${fractionParts.join("")}` : integer;
  };

  const validateEmail = (email) => {
    return /^\S+@\S+\.\S+$/.test(email.trim());
  };

  const parseErrorResponse = async (response) => {
    try {
      const text = await response.text();
      if (!text) return response.statusText || "Unknown error";
      try {
        const data = JSON.parse(text);
        return data.message || data.error || text;
      } catch {
        return text;
      }
    } catch {
      return response.statusText || "Unknown error";
    }
  };

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(normalizePhoneInput(value));
  };

  const updateVariantSelection = useCallback((variantId, field, value) => {
    setVariantSelections((prev) => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        [field]: value
      }
    }));
    setError("");
  }, []);

  const getVariantSelection = useCallback((variant) => {
    const existing = variantSelections[variant.variantId] || {};
    return {
      quantity: existing.quantity != null ? existing.quantity : "1",
      sellingPrice: existing.sellingPrice != null ? existing.sellingPrice : String(variant.offerPrice ?? variant.originalPrice ?? 0)
    };
  }, [variantSelections]);

  const addToOrderCart = useCallback((product, variant) => {
    const selection = getVariantSelection(variant);
    const quantity = parseInt(selection.quantity, 10);
    const sellingPrice = parseFloat(selection.sellingPrice);
    const maxStock = variant.stockQuantity || 0;

    if (!selection.quantity.trim()) {
      setError("Please enter quantity before adding to cart.");
      return;
    }
    if (Number.isNaN(quantity) || quantity < 1) {
      setError("Quantity must be a valid number greater than 0.");
      return;
    }
    if (!selection.sellingPrice.trim()) {
      setError("Please enter a selling price before adding to cart.");
      return;
    }
    if (Number.isNaN(sellingPrice) || sellingPrice <= 0) {
      setError("Selling price must be greater than 0.");
      return;
    }
    if (quantity > maxStock) {
      setError(`Cannot add ${quantity} items. Only ${maxStock} available in stock.`);
      return;
    }

    setError("");
    setOrderCart((prevCart) => {
      const existingIndex = prevCart.findIndex((item) => item.variantId === variant.variantId);

      if (existingIndex >= 0) {
        const existingItem = prevCart[existingIndex];
        const existingQuantity = parseInt(existingItem.quantity, 10) || 0;
        const newQuantity = existingQuantity + quantity;

        if (newQuantity > maxStock) {
          setError(`Total quantity cannot exceed available stock (${maxStock}).`);
          return prevCart;
        }

        const updatedCart = [...prevCart];
        updatedCart[existingIndex] = {
          ...existingItem,
          quantity: String(newQuantity),
          sellingPrice: String(sellingPrice)
        };
        return updatedCart;
      }

      return [
        ...prevCart,
        {
          variantId: variant.variantId,
          productName: product.productName,
          productImage: getMainImage(product),
          variant,
          quantity: String(quantity),
          sellingPrice: String(sellingPrice),
          maxStock
        }
      ];
    });
  }, [getVariantSelection]);

  const updateCartItemQuantity = useCallback((variantId, newQuantity) => {
    if (newQuantity < 1) return;
    setOrderCart((prevCart) =>
      prevCart.map((item) =>
        item.variantId === variantId
          ? {
              ...item,
              quantity: String(Math.min(newQuantity, item.maxStock))
            }
          : item
      )
    );
    setError("");
  }, []);

  const updateCartItemSellingPrice = useCallback((variantId, value) => {
    const sanitized = sanitizeDecimalValue(value);
    setOrderCart((prevCart) =>
      prevCart.map((item) =>
        item.variantId === variantId
          ? { ...item, sellingPrice: sanitized }
          : item
      )
    );
    setError("");
  }, []);

  const removeFromCart = useCallback((variantId) => {
    setOrderCart((prevCart) => prevCart.filter((item) => item.variantId !== variantId));
  }, []);

  const totals = useMemo(() => {
    const subtotal = orderCart.reduce((sum, item) => {
      const quantity = parseInt(item.quantity, 10) || 0;
      const price = parseFloat(item.sellingPrice) || 0;
      return sum + quantity * price;
    }, 0);

    const shippingCharge = 0;
    const discount = 0;
    const grandTotal = subtotal + shippingCharge - discount;
    const totalItems = orderCart.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    const totalVariants = orderCart.length;

    return {
      subtotal,
      shippingCharge,
      discount,
      grandTotal,
      totalItems,
      totalVariants
    };
  }, [orderCart]);

  const getStockStatus = (stockQuantity) => {
    if (stockQuantity === 0) return "out-of-stock";
    if (stockQuantity < 5) return "low-stock";
    return "good-stock";
  };

  const isAddressRequired = useMemo(() => {
    return paymentMethod === "COD" || !["SHOP", "DIRECT", "EXHIBITION"].includes(salesChannel);
  }, [paymentMethod, salesChannel]);

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      setError("Customer name is required.");
      return;
    }

    if (customerEmail.trim() && !validateEmail(customerEmail.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    const cleanPhone = normalizePhoneInput(phoneNumber);
    if (cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    const sanitizedPincode = sanitizeIntegerValue(shippingPincode, 6);
    if (shippingPincode.trim() && sanitizedPincode.length !== 6) {
      setError("Pincode must be 6 digits.");
      return;
    }

    if (isAddressRequired) {
      if (!shippingAddress.trim()) {
        setError("Shipping address is required for this order type.");
        return;
      }
      if (!shippingCity.trim()) {
        setError("Shipping city is required.");
        return;
      }
      if (!shippingPincode.trim()) {
        setError("Shipping pincode is required.");
        return;
      }
      if (sanitizedPincode.length !== 6) {
        setError("Shipping pincode must be 6 digits.");
        return;
      }
      if (!shippingState.trim()) {
        setError("Shipping state is required.");
        return;
      }
    }

    if (orderCart.length === 0) {
      setError("Please add at least one item to the order.");
      return;
    }

    const invalidItem = orderCart.find((item) => {
      const quantity = parseInt(item.quantity, 10) || 0;
      return quantity > item.maxStock;
    });

    if (invalidItem) {
      setError("One or more items exceed available stock. Please review quantities.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const orderData = {
        customerName: customerName.trim(),
        createdByName: createdByName.trim(),
        customerEmail: customerEmail.trim(),
        phoneNumber: cleanPhone,
        salesChannel,
        paymentMethod,
        shippingAddress: shippingAddress.trim(),
        shippingCity: shippingCity.trim(),
        shippingPincode: shippingPincode.trim(),
        shippingState: shippingState.trim(),
        totalAmount: totals.grandTotal,
        items: orderCart.map((item) => ({
          variantId: item.variantId,
          quantity: parseInt(item.quantity, 10) || 0,
          sellingPrice: parseFloat(item.sellingPrice) || 0
        }))
      };

      const response = await fetch(`${config.baseUrl}/api/orders/offlineOrder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorText = await parseErrorResponse(response);
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      let result = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }
      const orderId = result?.orderId || "N/A";
      onSuccess(result);
      onClose();
    } catch (err) {
      console.error("Offline order creation error:", err);
      setError(err.message || "Failed to create offline order.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h2>Create Offline Order</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          <div className="form-section">
            <h3>Customer Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  placeholder="10-digit phone number"
                />
              </div>
              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
              <div className="form-group">
                <label>Created By</label>
                <input
                  type="text"
                  value={createdByName}
                  onChange={(e) => setCreatedByName(e.target.value)}
                  placeholder="Admin name"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Order Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Payment Method</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="COD">Cash on Delivery</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sales Channel</label>
                <select value={salesChannel} onChange={(e) => setSalesChannel(e.target.value)}>
                  <option value="WHATSAPP">WhatsApp</option>
                  <option value="SHOP">Shop</option>
                  <option value="INSTAGRAM">Instagram</option>
                  <option value="EXHIBITION">Exhibition</option>
                  <option value="DIRECT">Direct</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              Shipping Address {isAddressRequired ? "" : <span className="optional-field">(Optional)</span>}
            </h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address{isAddressRequired ? " *" : ""}</label>
                <textarea
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="Enter shipping address"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>City{isAddressRequired ? " *" : ""}</label>
                <input
                  type="text"
                  value={shippingCity}
                  onChange={(e) => setShippingCity(e.target.value)}
                  placeholder="Enter city"
                />
              </div>
              <div className="form-group">
                <label>Pincode{isAddressRequired ? " *" : ""}</label>
                <input
                  type="text"
                  value={shippingPincode}
                  onChange={(e) => setShippingPincode(sanitizeIntegerValue(e.target.value, 6))}
                  placeholder="Enter pincode"
                />
              </div>
              <div className="form-group">
                <label>State{isAddressRequired ? " *" : ""}</label>
                <input
                  type="text"
                  value={shippingState}
                  onChange={(e) => setShippingState(e.target.value)}
                  placeholder="Enter state"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Selected Products - Choose Variants</h3>
            {selectedProducts.length === 0 ? (
              <p>No products selected.</p>
            ) : (
              <div className="selected-products-list">
                {selectedProducts.map((product) => (
                  <div key={product.productId} className="selected-product-card">
                    <div className="product-header">
                      <img src={getMainImage(product)} alt={product.productName} className="product-thumb" />
                      <div className="product-info">
                        <h4>{product.productName}</h4>
                        <p>{product.category?.name || product.categoryName || "N/A"}</p>
                      </div>
                    </div>

                    {product.variants?.length > 0 ? (
                      <div className="variants-table">
                        <div className="variants-header">
                          <span>Size</span>
                          <span>Color</span>
                          <span>SKU</span>
                          <span>Stock</span>
                          <span>Price</span>
                          <span>Qty</span>
                          <span>Sell Price</span>
                          <span>Action</span>
                        </div>
                        {product.variants.map((variant) => (
                          <VariantSelector
                            key={variant.variantId}
                            product={product}
                            variant={variant}
                            selection={getVariantSelection(variant)}
                            onSelectionChange={updateVariantSelection}
                            onAddToCart={addToOrderCart}
                            stockStatus={getStockStatus(variant.stockQuantity || 0)}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="no-variants">No variants available.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Order Cart ({totals.totalVariants} variants, {totals.totalItems} items)</h3>
            {orderCart.length === 0 ? (
              <div className="empty-cart">
                <p>No items added to the order yet.</p>
                <small>Select a variant and click Add To Order.</small>
              </div>
            ) : (
              <div className="order-cart">
                {orderCart.map((item) => (
                  <CartItem
                    key={item.variantId}
                    item={item}
                    onUpdateQuantity={updateCartItemQuantity}
                    onUpdatePrice={updateCartItemSellingPrice}
                    onRemove={removeFromCart}
                  />
                ))}
              </div>
            )}
          </div>

          {orderCart.length > 0 && (
            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-details">
                <div className="summary-item">
                  <span>Total Variants:</span>
                  <span>{totals.totalVariants}</span>
                </div>
                <div className="summary-item">
                  <span>Total Quantity:</span>
                  <span>{totals.totalItems}</span>
                </div>
                <div className="summary-item">
                  <span>Subtotal:</span>
                  <span>₹{totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.shippingCharge > 0 && (
                  <div className="summary-item">
                    <span>Shipping:</span>
                    <span>₹{totals.shippingCharge.toFixed(2)}</span>
                  </div>
                )}
                {totals.discount > 0 && (
                  <div className="summary-item">
                    <span>Discount:</span>
                    <span>-₹{totals.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="summary-item total">
                  <span>Grand Total:</span>
                  <span>₹{totals.grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading || orderCart.length === 0}
          >
            {loading ? "Creating Order..." : "Create Offline Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VariantSelector({ product, variant, selection, onSelectionChange, onAddToCart, stockStatus }) {
  const sanitizeDecimalInput = (value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (cleaned === ".") return "";
    const [integer, ...fractionParts] = cleaned.split(".");
    return fractionParts.length > 0 ? `${integer}.${fractionParts.join("")}` : integer;
  };

  const handleQuantityChange = (value) => {
    const normalized = value.replace(/[^0-9]/g, "");
    onSelectionChange(variant.variantId, "quantity", normalized);
  };

  const handleSellingPriceChange = (value) => {
    onSelectionChange(variant.variantId, "sellingPrice", sanitizeDecimalInput(value));
  };

  const isOutOfStock = (variant.stockQuantity || 0) === 0;

  return (
    <div className={`variant-row ${stockStatus}`}>
      <span className="variant-size">{variant.size?.code || variant.sizeName || "N/A"}</span>
      <span className="variant-color">{variant.color || "N/A"}</span>
      <span className="variant-sku">{variant.sku}</span>
      <span className={`variant-stock ${stockStatus}`}>{variant.stockQuantity || 0}</span>
      <span className="variant-price">
        ₹{Number(variant.originalPrice || 0).toFixed(2)}
        {variant.offerPrice && variant.offerPrice !== variant.originalPrice && (
          <small className="offer-price">₹{Number(variant.offerPrice).toFixed(2)}</small>
        )}
      </span>
      <div className="variant-quantity">
        <input
          type="text"
          inputMode="numeric"
          value={selection.quantity}
          onChange={(e) => handleQuantityChange(e.target.value)}
          disabled={isOutOfStock}
          placeholder="Qty"
        />
      </div>
      <div className="variant-selling-price">
        <input
          type="text"
          inputMode="decimal"
          value={selection.sellingPrice}
          onChange={(e) => handleSellingPriceChange(e.target.value)}
          disabled={isOutOfStock}
          placeholder="Price"
        />
      </div>
      <div className="variant-action">
        {isOutOfStock ? (
          <span className="out-of-stock-text">Out of Stock</span>
        ) : (
          <button
            className="btn-add-to-order"
            onClick={() => onAddToCart(product, variant)}
            disabled={
              isOutOfStock ||
              !selection.quantity.trim() ||
              !selection.sellingPrice.trim() ||
              Number.isNaN(parseInt(selection.quantity, 10)) ||
              Number.parseInt(selection.quantity, 10) < 1 ||
              Number.isNaN(parseFloat(selection.sellingPrice)) ||
              Number.parseFloat(selection.sellingPrice) <= 0
            }
          >
            Add To Order
          </button>
        )}
      </div>
    </div>
  );
}

function CartItem({ item, onUpdateQuantity, onUpdatePrice, onRemove }) {
  const quantity = parseInt(item.quantity, 10) || 0;
  const remainingStock = item.maxStock - quantity;

  const handleIncrement = () => {
    const nextQuantity = Math.min(quantity + 1, item.maxStock);
    onUpdateQuantity(item.variantId, nextQuantity);
  };

  const handleDecrement = () => {
    const nextQuantity = Math.max(quantity - 1, 1);
    onUpdateQuantity(item.variantId, nextQuantity);
  };

  return (
    <div className="cart-item">
      <div className="cart-item-info">
        <img src={item.productImage} alt={item.productName} className="cart-item-image" />
        <div className="cart-item-details">
          <h5>{item.productName}</h5>
          <p>
            Size: {item.variant.size?.code || item.variant.sizeName || "N/A"} | Color: {item.variant.color || "N/A"} | SKU: {item.variant.sku}
          </p>
          <div className="stock-info">
            <small>Stock: {item.maxStock} | In Cart: {quantity} | Remaining: {remainingStock}</small>
          </div>
        </div>
      </div>

      <div className="cart-item-controls">
        <div className="cart-quantity">
          <label>Qty</label>
          <div className="quantity-controls">
            <button className="qty-btn" onClick={handleDecrement} disabled={quantity <= 1}>
              -
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={item.quantity}
              onChange={(e) => onUpdateQuantity(item.variantId, parseInt(e.target.value.replace(/\D/g, ""), 10) || 1)}
            />
            <button className="qty-btn" onClick={handleIncrement} disabled={quantity >= item.maxStock}>
              +
            </button>
          </div>
        </div>

        <div className="cart-price">
          <label>Price</label>
          <input
            type="text"
            inputMode="decimal"
            value={item.sellingPrice}
            onChange={(e) => onUpdatePrice(item.variantId, e.target.value)}
          />
        </div>

        <div className="cart-subtotal">
          ₹{((parseFloat(item.sellingPrice) || 0) * quantity).toFixed(2)}
        </div>

        <button className="btn-remove-cart" onClick={() => onRemove(item.variantId)}>
          ✕
        </button>
      </div>
    </div>
  );
}

export default OfflineOrderModal;
